import Image from 'next/image';
import {MdBusiness, MdOutlineCardTravel} from 'react-icons/md';
import {
  COLOR_BLACK_4,
  COLOR_PRIMARY_1,
} from '../../../../../config/constants/colorCodes';
import type {
  RecommendedRecruitment,
  SimilarRecruitment,
} from '../../../../../core/entities/recruitment/schema';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {appendQueryParam} from '../../../../../shared/services/appendQueryParam';

const TAG_COMMON_CLASS_NAME =
  'text-xs py-2 px-8 min-w-[72px] text-center leading';

// リデザイン前のコンポーネント
export const GridRecruitmentList: React.FC<{
  sectionTitle: string;
  recruitmentList: SimilarRecruitment[] | RecommendedRecruitment[];
}> = ({sectionTitle, recruitmentList}) => {
  return (
    <>
      <h2 className="bg-primary-1 p-12 text-white text-md leading font-bold">
        {sectionTitle}
      </h2>
      <div className="bg-white p-24 grid grid-cols-2">
        {recruitmentList.map((recruitment, i) => {
          const entryButtonUrl = recruitment.entry_url
            ? appendQueryParam(
                recruitment.entry_url,
                'source_url',
                recruitment.detail_page_url,
              )
            : '';

          return (
            i < 4 && (
              <div className="w-full h-auto min-h-[196px] [&:nth-child(odd)]:pr-12 [&:nth-child(even)]:pl-12 [&:nth-child(1)]:border-b-[1px] [&:nth-child(2)]:border-b-[1px] border-line border-collapse [&:nth-child(1)]:mb-24 [&:nth-child(2)]:mb-24 [&:nth-child(1)]:pb-16 [&:nth-child(2)]:pb-16">
                <ul className="flex flex-wrap">
                  <RecruitingItemTypeTag
                    recruitingItemType={recruitment.recruiting_item_type}
                  />
                  <Tag
                    key={`tag-industry-${recruitment.company.industry.id}`}
                    name={recruitment.company.industry.name}
                  />
                </ul>
                <div className="flex pt-8">
                  <div className="w-[50px] h-[50px]">
                    {recruitment.company.logo_url ? (
                      <Image
                        className="object-contain"
                        src={recruitment.company.logo_url}
                        alt={recruitment.company.name}
                        width={50}
                        height={50}
                      />
                    ) : (
                      <div className="rounded-full border-line border-[1px] border-collapse w-full h-full flex justify-center items-center">
                        <MdBusiness size={30} fill={COLOR_BLACK_4} />
                      </div>
                    )}
                  </div>
                  <div className="w-[calc(100%-58px)] pl-8">
                    <CommonLink
                      className="text-base text-link leading font-bold pc:hover:opacity-80 line-clamp-2"
                      href={recruitment.detail_page_url}
                      target="_blank"
                    >
                      {recruitment.title}
                    </CommonLink>
                    {recruitment.job_types.length > 0 && (
                      <div className="p-8 bg-background-1 flex items-center mt-8">
                        <div className="block mr-8">
                          <MdOutlineCardTravel
                            size={14}
                            fill={COLOR_PRIMARY_1}
                          />
                        </div>
                        <div className="break-all flex items-center flex-wrap">
                          {recruitment.job_types.map((jobType, i) => (
                            <span className="text-primary-1 text-sm mr-4">
                              {i === 0
                                ? jobType.name
                                : `/${' '}${jobType.name}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-black-4 text-sm leading mt-8">
                      {recruitment.company.name}
                    </p>
                    {recruitment.can_entry && (
                      <div className="flex flex-wrap mt-8">
                        <CommonLink
                          className="block text-base text-white font-bold py-4 px-16 bg-accent-1 rounded shadow-button leading pc:hover:opacity-80"
                          href={entryButtonUrl}
                          target="_blank"
                        >
                          エントリーする
                        </CommonLink>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          );
        })}
      </div>
    </>
  );
};

const RecruitingItemTypeTag: React.FC<{recruitingItemType: string}> = ({
  recruitingItemType,
}) => {
  let bgClassName = 'bg-black-3';
  let label = '';
  switch (recruitingItemType) {
    case 'selection': {
      bgClassName = 'bg-category-1';
      label = '本選考';
      break;
    }

    case 'intern': {
      bgClassName = 'bg-category-7';
      label = 'インターン';
      break;
    }

    case 'event': {
      bgClassName = 'bg-category-3';
      label = 'イベント';
      break;
    }

    case 'jointEvent': {
      bgClassName = 'bg-category-3';
      label = '合同イベント';
      break;
    }

    case 'gssktEvent': {
      bgClassName = 'bg-category-3';
      label = '外資就活主催';
      break;
    }

    default: {
      break;
    }
  }

  return (
    <li className={`${bgClassName} ${TAG_COMMON_CLASS_NAME} text-white mr-12`}>
      {label}
    </li>
  );
};

const Tag: React.FC<{name: string}> = ({name}) => {
  return (
    <li
      className={`bg-white ${TAG_COMMON_CLASS_NAME} text-black-1 border-[1px] border-black-1 border-collapse`}
    >
      {name}
    </li>
  );
};
